/**
 * SldWire — custom edge that renders as an orthogonal (L-bend / step) path
 * coloured by electrical type (DC+ red, AC black, earth green).
 *
 * Click the wire to select it → hit Delete/Backspace to remove it.
 * Selected wires render thicker and slightly brighter.
 */
import { memo } from 'react';
import { EdgeProps, BaseEdge, getSmoothStepPath, getStraightPath, EdgeLabelRenderer, useReactFlow } from '@xyflow/react';
import type { SldEdge } from '../store/editor';
import { TYPE_COLORS, preferredWireStyle, type TerminalType } from '../types/symbol';
import { useEditorStore } from '../store/editor';
import { SYMBOLS } from '../symbols/library';

// Mirror of the specificity table in types/symbol.ts. Local copy so
// the chain-walk below can compare styles without an extra import or
// helper call per iteration.
const SPECIFICITY_LOOKUP: Record<TerminalType, number> = {
  'dc+':   3,
  'dc-':   3,
  'ac':    3,
  'earth': 3,
  'dc':    1,
  'any':   0,
};

export const SldWire = memo((props: EdgeProps<SldEdge>) => {
  const {
    sourceX, sourceY, targetX, targetY,
    sourcePosition, targetPosition,
    sourceHandleId, targetHandleId, source, target,
    data, selected, id
  } = props;

  // Resolve the wire's effective style. We start with whatever is
  // saved on data.style — usually correct because onConnect / auto-
  // layout assign it. But for legacy wires that were saved with the
  // unpolarised 'dc' or 'any' tag (pre-fix onConnect, or the user
  // happened to drag from the unpolarised end), promote to the more
  // specific terminal type at RENDER time so the wire renders red /
  // green / etc when one of its endpoints actually carries dc+/dc-/
  // earth. This makes the fix transparent to existing diagrams.
  const savedStyle = (data?.style ?? 'any') as TerminalType;
  const resolvedStyle = useEditorStore((s) => {
    // Only attempt promotion when the saved style is genericish — no
    // point looking up nodes for a wire that's already specific.
    if (savedStyle !== 'any' && savedStyle !== 'dc') return savedStyle;

    // (1) Terminal-derived: look up the connected terminal types and
    //     pick the more specific one. Catches panel-string ↔ inverter
    //     wires where one end is dc+/dc- and the other is dc.
    const srcNode = s.nodes.find((n) => n.id === source);
    const tgtNode = s.nodes.find((n) => n.id === target);
    const srcSym = srcNode ? SYMBOLS[srcNode.data.symbolId] : undefined;
    const tgtSym = tgtNode ? SYMBOLS[tgtNode.data.symbolId] : undefined;
    const srcType = srcSym && sourceHandleId ? srcSym.terminals[sourceHandleId]?.type : undefined;
    const tgtType = tgtSym && targetHandleId ? tgtSym.terminals[targetHandleId]?.type : undefined;
    let candidate: TerminalType | undefined;
    if (srcType || tgtType) {
      candidate = preferredWireStyle(srcType ?? savedStyle, tgtType ?? savedStyle);
    }
    // If terminals nailed it down (dc+ / dc- / ac / earth), we're done.
    if (candidate && candidate !== 'dc' && candidate !== 'any') return candidate;

    // (2) Chain-derived: walk the connected component (BFS over edges
    //     that share a node) collecting the MOST specific style we
    //     find on any neighbouring wire. This is what propagates a
    //     panel-string's red colour through DC isolators / fuses /
    //     optimisers / inverter inputs even though those terminals
    //     are intentionally typed 'any'.
    const visitedEdges = new Set<string>([id]);
    const visitedNodes = new Set<string>([source, target]);
    const queue: string[] = [source, target];
    let bestSpecificity = 0;
    let bestStyle: TerminalType = candidate ?? savedStyle;
    while (queue.length) {
      const n = queue.shift()!;
      for (const e of s.edges) {
        if (visitedEdges.has(e.id)) continue;
        if (e.source !== n && e.target !== n) continue;
        visitedEdges.add(e.id);
        const eStyle = (e.data?.style ?? 'any') as TerminalType;
        const spec = SPECIFICITY_LOOKUP[eStyle];
        if (spec > bestSpecificity) {
          bestSpecificity = spec;
          bestStyle = eStyle;
          // Once we find a polarised style (specificity 3), stop —
          // can't get more specific than that.
          if (spec >= 3) return bestStyle;
        }
        const other = e.source === n ? e.target : e.source;
        if (!visitedNodes.has(other)) {
          visitedNodes.add(other);
          queue.push(other);
        }
      }
    }
    return bestStyle;
  });

  const color = TYPE_COLORS[resolvedStyle] ?? '#0f172a';

  // When source + target are nearly aligned (same Y for a horizontal run,
  // or same X for a vertical drop) use a STRAIGHT path. getSmoothStepPath
  // injects a fixed-offset bend even for perfectly aligned endpoints,
  // which produced a visible S-snake on the inverter↔IMS wire and other
  // short axis-aligned runs. The 1px tolerance covers float rounding.
  const ALIGN_TOL = 1;
  const isYAligned = Math.abs(sourceY - targetY) < ALIGN_TOL;
  const isXAligned = Math.abs(sourceX - targetX) < ALIGN_TOL;
  const [edgePath, labelX, labelY] = (isYAligned || isXAligned)
    ? getStraightPath({ sourceX, sourceY, targetX, targetY })
    : getSmoothStepPath({
        sourceX, sourceY, sourcePosition,
        targetX, targetY, targetPosition,
        borderRadius: 0,
      });

  const strokeWidth = selected ? 2.2 : 1.2;
  const strokeOpacity = selected ? 1 : 0.95;

  // The × delete button only makes sense for a SINGLE selection
  // (click-the-wire → click-×). When the user box-selects with Cmd/Ctrl
  // + drag, every captured wire would render its own × button — and
  // each one only deletes that single wire, which is misleading in a
  // multi-select context. Hide the × whenever any other item is
  // selected; Backspace still works for the multi-select case.
  const isOnlySelection = useEditorStore((s) => {
    if (!selected) return false;
    let otherEdges = 0;
    for (const e of s.edges) {
      if (e.selected && e.id !== id) { otherEdges++; break; }
    }
    if (otherEdges > 0) return false;
    for (const n of s.nodes) {
      if (n.selected && n.id !== '__page_frame__') return false;
    }
    return true;
  });

  return (
    <>
      {/* Invisible wide stroke for easier click targeting */}
      <path
        d={edgePath}
        fill="none"
        stroke="transparent"
        strokeWidth={14}
        style={{ cursor: 'pointer' }}
        className="react-flow__edge-interaction"
      />
      <BaseEdge
        id={id}
        path={edgePath}
        style={{
          stroke: color,
          strokeWidth,
          strokeOpacity,
          // Round caps + joins so wire endpoints meld cleanly into
          // symbol bodies (no square notch on the last pixel) and
          // L-bends don't show a mitred corner.
          strokeLinecap: 'round',
          strokeLinejoin: 'round'
        }}
      />
      {data?.label && (
        <WireLabel
          text={data.label}
          color={color}
          // Offset above the path midpoint so the label sits on top of
          // the wire rather than dead-centre on it. When the × delete
          // button is also showing (single-select) we push the label
          // up a bit further to avoid collision; otherwise a small
          // offset is enough to look intentional rather than crossed.
          labelX={labelX}
          labelY={labelY - (isOnlySelection ? 22 : 10)}
        />
      )}
      {isOnlySelection && <SelectedDeleteButton edgeId={id} labelX={labelX} labelY={labelY} />}
    </>
  );
});

/**
 * Cable label rendered at (or just above) the wire's midpoint. Small
 * white pill with the wire's resolved colour as the border so it reads
 * as "annotation on this wire" at a glance. Non-interactive — the wire
 * underneath still receives clicks for selection.
 */
function WireLabel({
  text, color, labelX, labelY,
}: { text: string; color: string; labelX: number; labelY: number }) {
  return (
    <EdgeLabelRenderer>
      <div
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          background: '#ffffff',
          color: '#0f172a',
          border: `1px solid ${color}`,
          borderRadius: 3,
          padding: '1px 5px',
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.25,
          fontFamily: 'system-ui, -apple-system, sans-serif',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
          maxWidth: 220,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        title={text}
      >
        {text}
      </div>
    </EdgeLabelRenderer>
  );
}

/**
 * The little red × that appears on a selected wire. It's a real button:
 * click to delete that one wire (with an undo entry pushed first so
 * Cmd/Ctrl+Z brings it back). Used to be display-only with
 * pointer-events:none; users reasonably assumed it was interactive
 * and were confused when clicking it did nothing.
 */
function SelectedDeleteButton({
  edgeId, labelX, labelY
}: { edgeId: string; labelX: number; labelY: number }) {
  const { deleteElements } = useReactFlow();
  const pushHistory = useEditorStore((s) => s.pushHistory);

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    pushHistory();
    deleteElements({ edges: [{ id: edgeId }] });
  };

  return (
    <EdgeLabelRenderer>
      <button
        type="button"
        onClick={handleDelete}
        title="Delete this wire (or press Delete/Backspace)"
        style={{
          position: 'absolute',
          transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
          background: '#ef4444',
          color: 'white',
          border: 'none',
          borderRadius: '50%',
          width: 20,
          height: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 700,
          cursor: 'pointer',
          // Re-enable pointer events for THIS one element. React Flow's
          // edge layer disables them globally so clicks land on the wire
          // itself; opting back in here makes the button responsive.
          pointerEvents: 'all',
          boxShadow: '0 1px 4px rgba(0,0,0,0.35)',
          padding: 0,
          lineHeight: 1
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        ×
      </button>
    </EdgeLabelRenderer>
  );
}
SldWire.displayName = 'SldWire';
